import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './App.css';

const API = axios.create({ baseURL: 'http://localhost:5000/api' });

// ── Helpers ────────────────────────────────────────────────────────────────
function formatRelativeTime(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const days = Math.floor(diff / 86400);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
}

// ── Role Permissions ───────────────────────────────────────────────────────
const PERMISSIONS = {
  admin: {
    canCreateTicket: true, canUpdateStatus: true, canAssignAgent: true,
    canEscalate: true, canPauseSLA: true, canViewAllTickets: true,
    canManageRules: true, canManageFields: true, canDeleteRules: true,
    canViewReports: true, canViewAgents: true, canBulkAction: true,
    canAddInternalNote: true, canSubmitCSAT: false, canReopenTicket: true,
    canManageKB: true,
  },
  agent: {
    canCreateTicket: true, canUpdateStatus: true, canAssignAgent: true,
    canEscalate: true, canPauseSLA: true, canViewAllTickets: true,
    canManageRules: true, canManageFields: false, canDeleteRules: false,
    canViewReports: true, canViewAgents: true, canBulkAction: true,
    canAddInternalNote: true, canSubmitCSAT: false, canReopenTicket: true,
    canManageKB: true,
  },
  requester: {
    canCreateTicket: true, canUpdateStatus: false, canAssignAgent: false,
    canEscalate: false, canPauseSLA: false, canViewAllTickets: false,
    canManageRules: false, canManageFields: false, canDeleteRules: false,
    canViewReports: false, canViewAgents: false, canBulkAction: false,
    canAddInternalNote: false, canSubmitCSAT: true, canReopenTicket: false,
    canManageKB: false,
  },
};

function can(role, action) {
  return PERMISSIONS[role]?.[action] ?? false;
}

// ── App Component ──────────────────────────────────────────────────────────
function App() {
  const [userRole, setUserRole] = useState('admin');
  const [tickets, setTickets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [users, setUsers] = useState([]);
  const [cannedResponses, setCannedResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterAgent, setFilterAgent] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({});
  const [quickReply, setQuickReply] = useState('');
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showCSATModal, setShowCSATModal] = useState(false);
  const [csatScore, setCSATScore] = useState(0);
  const [csatFeedback, setCSATFeedback] = useState('');
  const [trendData, setTrendData] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [showOverdueModal, setShowOverdueModal] = useState(false);
  const [overdueTickets, setOverdueTickets] = useState([]);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [autoRules, setAutoRules] = useState([]);
  const [newRule, setNewRule] = useState({ ConditionType: 'priority', ConditionValue: '', AgentID: '' });
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [agentPerformance, setAgentPerformance] = useState([]);
  const [avgFirstResponse, setAvgFirstResponse] = useState(0);
  const [avgResolution, setAvgResolution] = useState(0);
  const [statusBreakdown, setStatusBreakdown] = useState([]);
  const [savedFilters, setSavedFilters] = useState([]);
  const [showSaveFilter, setShowSaveFilter] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [escalateReason, setEscalateReason] = useState('');
  const [showPauseTimer, setShowPauseTimer] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [customFieldsModal, setCustomFieldsModal] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [newCustomField, setNewCustomField] = useState({ name: '', type: 'text', options: '' });
  const [newComment, setNewComment] = useState('');
  const [showInternalNotes, setShowInternalNotes] = useState(false);
  const [activeNav, setActiveNav] = useState('tickets');
  const [showReports, setShowReports] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [notification, setNotification] = useState(null);
  const [bulkAssignAgentID, setBulkAssignAgentID] = useState('');
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [agentsView, setAgentsView] = useState([]);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 15;
  const fileInputRef = useRef(null);

  // ── Knowledge Base state ──────────────────────────────────────────────
  const [kbArticles, setKBArticles] = useState([]);
  const [kbSearch, setKBSearch] = useState('');
  const [kbCategory, setKBCategory] = useState('all');
  const [kbCategories, setKBCategories] = useState([]);
  const [kbLoading, setKBLoading] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [showCreateArticleModal, setShowCreateArticleModal] = useState(false);
  const [newArticle, setNewArticle] = useState({ Title: '', Summary: '', Content: '', Category: 'General', Tags: '' });
  const [articleVoted, setArticleVoted] = useState({});

  // ── AI Chatbot state ──────────────────────────────────────────────────
  const [showChatbot, setShowChatbot] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', content: "Hi! I'm ServiceNet's AI assistant. How can I help you today?", timestamp: new Date().toISOString() }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  // ── Intelligent Routing state ─────────────────────────────────────────
  const [agentWorkload, setAgentWorkload] = useState([]);
  const [showWorkloadModal, setShowWorkloadModal] = useState(false);
  const [routingEnabled, setRoutingEnabled] = useState(true);

  // ── SLA Enhancement state ─────────────────────────────────────────────
  const [slaPolicies, setSLAPolicies] = useState([]);
  const [showSLAModal, setShowSLAModal] = useState(false);
  const [editingSLA, setEditingSLA] = useState(null);
  const [slaDetails, setSLADetails] = useState(null);

  const [newTicket, setNewTicket] = useState({
    Title: '', Description: '', Priority: 'medium', Category: '',
  });

  // ── Current Requester (for requester mode) ────────────────────────────
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const saved = localStorage.getItem('currentUser');
      return saved ? JSON.parse(saved) : { id: 4, name: 'Guest User', email: 'guest@example.com', role: 'requester' };
    } catch { return { id: 4, name: 'Guest User', email: 'guest@example.com', role: 'requester' }; }
  });

  // ── Current Agent (for agent mode) ───────────────────────────────────
  const [currentAgent, setCurrentAgent] = useState(() => {
    try {
      const saved = localStorage.getItem('currentAgent');
      return saved ? JSON.parse(saved) : { id: 1, name: 'Agent', email: 'agent@servicenet.com', role: 'agent' };
    } catch { return { id: 1, name: 'Agent', email: 'agent@servicenet.com', role: 'agent' }; }
  });

  // ── Notifications ──────────────────────────────────────────────────────
  const showNotification = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3500);
  };

  // ── Data Loading ───────────────────────────────────────────────────────
  useEffect(() => {
    loadAllData();
    loadTrendData();
    loadAutoRules();
    loadAgentPerformance();
    loadCustomFields();
    loadSavedFilters();
    loadSLAPolicies();
    loadKBArticles();
    loadKBCategories();
    loadAgentWorkload();
    loadUsers();
  }, []);

  // Re-load tickets when role, currentUser, or currentAgent changes
  useEffect(() => {
    loadTickets();
    setFilterStatus('all'); setFilterPriority('all');
    setFilterAgent('all'); setFilterCategory('all');
    setSearchTerm(''); setActiveNav('tickets'); setShowReports(false);
  }, [userRole, currentUser.id, currentAgent.id]);

  useEffect(() => {
    if (showChatbot && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, showChatbot]);

  const loadAllData = async () => {
    await loadTickets();
    await loadStats();
    await loadAgents();
    await loadCannedResponses();
  };

  const loadTickets = async () => {
    try {
      setLoading(true);
      const res = await API.get('/tickets');
      let data = res.data || [];
      // No role-based filtering here — filtering is done in getFilteredTickets()
      setTickets(data);
      const frTimes = data.filter(t => t.FirstResponseAt).map(t =>
        (new Date(t.FirstResponseAt) - new Date(t.CreatedAt)) / 60000);
      setAvgFirstResponse(frTimes.length ? Math.round(frTimes.reduce((a,b) => a+b,0)/frTimes.length) : 0);
      const resTimes = data.filter(t => t.ResolvedAt).map(t =>
        (new Date(t.ResolvedAt) - new Date(t.CreatedAt)) / 3600000);
      setAvgResolution(resTimes.length ? Math.round(resTimes.reduce((a,b) => a+b,0)/resTimes.length) : 0);
      setStatusBreakdown([
        { name: 'Open',     value: data.filter(t => t.Status==='open').length,        color: '#6366f1' },
        { name: 'Pending',  value: data.filter(t => t.Status==='in_progress').length, color: '#f59e0b' },
        { name: 'Resolved', value: data.filter(t => t.Status==='resolved').length,    color: '#10b981' },
        { name: 'Closed',   value: data.filter(t => t.Status==='closed').length,      color: '#64748b' },
      ]);
      setLoading(false);
    } catch { setLoading(false); }
  };

  const loadStats          = async () => { try { const r = await API.get('/dashboard/stats');  setStats(r.data||{}); } catch {} };
  const loadAgents         = async () => { try { const r = await API.get('/agents');            setAgents(r.data||[]); setAgentsView(r.data||[]); } catch {} };
  const loadCannedResponses= async () => { try { const r = await API.get('/canned-responses'); setCannedResponses(r.data||[]); } catch {} };
  const loadTrendData      = async () => { try { const r = await API.get('/tickets/trend');    setTrendData(r.data||[]); } catch {} };
  const loadOverdueTickets = async () => { try { const r = await API.get('/tickets/overdue');  setOverdueTickets(r.data||[]); } catch {} };
  const loadAgentPerformance=async () => { try { const r = await API.get('/agents/performance'); setAgentPerformance(r.data||[]); } catch {} };
  const loadAutoRules      = async () => { try { const r = await API.get('/auto-assign-rules'); setAutoRules(r.data||[]); } catch {} };
  const loadCustomFields   = async () => { try { const r = await API.get('/custom-fields');   setCustomFields(r.data||[]); } catch { setCustomFields([]); } };
  const loadSavedFilters   = ()        => { try { const s = localStorage.getItem('savedTicketFilters'); if (s) setSavedFilters(JSON.parse(s)); } catch {} };
  const loadSLAPolicies    = async () => { try { const r = await API.get('/sla-policies');    setSLAPolicies(r.data||[]); } catch {} };
  const loadSLADetails     = async (id)=> { try { const r = await API.get(`/tickets/${id}/sla`); setSLADetails(r.data); } catch {} };
  const loadAgentWorkload  = async () => { try { const r = await API.get('/agents/workload'); setAgentWorkload(r.data||[]); } catch {} };

  const loadUsers = async () => {
    try {
      const r = await API.get('/users');
      setUsers(r.data || []);
    } catch (e) { console.error('loadUsers error:', e); }
  };

  const loadComments = useCallback(async (ticketId, includeInternal) => {
    try {
      const r = await API.get(`/tickets/${ticketId}/comments?includeInternal=${!!includeInternal}`);
      setComments(r.data||[]);
    } catch {}
  }, []);

  const loadAttachments = async (ticketId) => {
    try { const r = await API.get(`/tickets/${ticketId}/attachments`); setAttachments(r.data||[]); } catch {}
  };

  const loadKBArticles = async (search='', category='all') => {
    setKBLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (category !== 'all') params.category = category;
      const r = await API.get('/kb/articles', { params });
      setKBArticles(r.data||[]);
    } catch { setKBArticles([]); }
    setKBLoading(false);
  };
  const loadKBCategories = async () => { try { const r = await API.get('/kb/categories'); setKBCategories(r.data||[]); } catch {} };

  // ── Switcher helpers ───────────────────────────────────────────────────
  const handleSwitchRequester = (userId) => {
    const user = users.find(u => u.UserID === parseInt(userId));
    if (!user) return;
    const next = { id: user.UserID, name: user.FullName, email: user.Email, role: user.Role };
    setCurrentUser(next);
    localStorage.setItem('currentUser', JSON.stringify(next));
  };

  const handleSwitchAgent = (agentId) => {
    const agent = agents.find(a => a.UserID === parseInt(agentId));
    if (!agent) return;
    const next = { id: agent.UserID, name: agent.FullName, email: agent.Email, role: 'agent' };
    setCurrentAgent(next);
    localStorage.setItem('currentAgent', JSON.stringify(next));
  };

  // ── Actions ────────────────────────────────────────────────────────────
  const handleCreateTicket = async (e) => {
    e.preventDefault();
    const requesterId = userRole === 'requester' ? currentUser.id : 4;
    try {
      await API.post('/tickets', { ...newTicket, RequesterID: requesterId });
      setShowCreateForm(false);
      setNewTicket({ Title:'', Description:'', Priority:'medium', Category:'' });
      loadAllData(); loadAgentWorkload();
      showNotification(routingEnabled ? 'Ticket created & auto-routed to best available agent' : 'Ticket created successfully');
    } catch { showNotification('Error creating ticket', 'error'); }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    if (!can(userRole, 'canUpdateStatus')) return;
    try {
      await API.patch(`/tickets/${ticketId}/status`, { Status: newStatus });
      loadAllData();
      if (selectedTicket?.TicketID === ticketId) setSelectedTicket(prev => ({...prev, Status: newStatus}));
      showNotification(`Status updated to ${newStatus}`);
    } catch { showNotification('Error updating status', 'error'); }
  };

  const handleAssignAgent = async (ticketId, agentId) => {
    if (!can(userRole, 'canAssignAgent') || !agentId) return;
    try {
      await API.patch(`/tickets/${ticketId}/assign`, { AgentID: agentId });
      loadAllData();
      showNotification('Agent assigned');
    } catch { showNotification('Error assigning agent', 'error'); }
  };

const handleQuickReply = async () => {
  if (!selectedTicket || !quickReply.trim()) return;
  const userId = userRole === 'requester' ? currentUser.id : (userRole === 'agent' ? currentAgent.id : 1);
  try {
    await API.post(`/tickets/${selectedTicket.TicketID}/comments`, { Comment: quickReply, UserID: userId, IsInternal: false });
    setQuickReply('');
    // Refresh comments for the right panel
    await loadComments(selectedTicket.TicketID, showInternalNotes);
    loadAllData();
    showNotification('Reply sent');
  } catch (error) { 
    console.error('Reply error:', error);
    showNotification('Error sending reply', 'error'); 
  }
};

  const applyCannedResponse = async (responseId) => {
    if (!selectedTicket) return;
    try { const r = await API.get(`/canned-responses/${responseId}`); setQuickReply(r.data.Content); showNotification('Canned response loaded'); }
    catch { showNotification('Error', 'error'); }
  };

  const handleEscalateTicket = async () => {
    if (!selectedTicket) return;
    try {
      await API.patch(`/tickets/${selectedTicket.TicketID}/escalate`, { Reason: escalateReason });
      setShowEscalateModal(false); setEscalateReason(''); loadAllData();
      showNotification('Ticket escalated');
    } catch { showNotification('Error', 'error'); }
  };

  const handlePauseSLA = async () => {
    if (!selectedTicket) return;
    try {
      await API.post(`/tickets/${selectedTicket.TicketID}/pause-sla`, { Reason: pauseReason });
      setShowPauseTimer(false); setPauseReason(''); loadAllData();
      showNotification('SLA paused (deadline extended by 24h)');
    } catch { showNotification('Error', 'error'); }
  };

  const handleAddComment = async (isInternal = false) => {
  if (!newComment.trim() || !selectedTicket) return;
  const userId = userRole === 'requester' ? currentUser.id : (userRole === 'agent' ? currentAgent.id : 1);
  try {
    await API.post(`/tickets/${selectedTicket.TicketID}/comments`, { Comment: newComment, UserID: userId, IsInternal: isInternal });
    // ...
      setNewComment(''); loadComments(selectedTicket.TicketID, showInternalNotes);
      showNotification('Comment added');
    } catch { showNotification('Error', 'error'); }
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !selectedTicket) return;
    const fd = new FormData(); fd.append('file', selectedFile);
    try {
      await API.post(`/tickets/${selectedTicket.TicketID}/attachments`, fd);
      setSelectedFile(null); loadAttachments(selectedTicket.TicketID);
      showNotification('File uploaded');
    } catch { showNotification('Error', 'error'); }
  };

  const handleCSATSubmit = async () => {
    if (!selectedTicket) return;
    try {
      await API.post(`/tickets/${selectedTicket.TicketID}/csat`, { Score: csatScore, Feedback: csatFeedback });
      setShowCSATModal(false); setCSATScore(0); setCSATFeedback('');
      showNotification('Thank you for your feedback!');
    } catch { showNotification('Error', 'error'); }
  };

  const handleExport = async () => {
    try { const r = await API.get('/export/tickets'); setExportData(r.data||[]); setShowExportModal(true); }
    catch { showNotification('Error', 'error'); }
  };

  const downloadCSV = () => {
    const headers = ['Ticket Number','Title','Status','Priority','Category','Requester','Agent','Created Date','Resolution Hours'];
    const rows = exportData.map(t => [
      t.TicketNumber, `"${t.Title}"`, t.Status, t.Priority,
      t.Category, t.RequesterName, t.AgentName||'',
      new Date(t.CreatedAt).toLocaleDateString(), t.ResolutionHours||'-',
    ]);
    const csv = [headers,...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `tickets_${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const handleAddAutoRule = async () => {
    if (!newRule.ConditionValue || !newRule.AgentID) { showNotification('Please fill all fields','error'); return; }
    try {
      await API.post('/auto-assign-rules', newRule); loadAutoRules();
      setNewRule({ ConditionType:'priority', ConditionValue:'', AgentID:'' });
      showNotification('Rule added');
    } catch { showNotification('Error','error'); }
  };

  const handleDeleteRule = async (ruleId) => {
    try { await API.delete(`/auto-assign-rules/${ruleId}`); loadAutoRules(); showNotification('Rule deleted'); }
    catch { showNotification('Error','error'); }
  };

  const handleAddCustomField = async () => {
    if (!newCustomField.name) { showNotification('Field name required','error'); return; }
    try {
      await API.post('/custom-fields', newCustomField); loadCustomFields();
      setNewCustomField({ name:'', type:'text', options:'' });
      showNotification('Field added');
    } catch { showNotification('Error','error'); }
  };

  const saveCurrentFilter = () => {
    if (!filterName) return;
    const newF = { name:filterName, status:filterStatus, priority:filterPriority, agent:filterAgent, category:filterCategory, search:searchTerm };
    const updated = [...savedFilters, newF];
    setSavedFilters(updated);
    localStorage.setItem('savedTicketFilters', JSON.stringify(updated));
    setShowSaveFilter(false); setFilterName('');
    showNotification('Filter saved');
  };

  const deleteSavedFilter = (idx) => {
    const updated = savedFilters.filter((_,i) => i!==idx);
    setSavedFilters(updated);
    localStorage.setItem('savedTicketFilters', JSON.stringify(updated));
  };

  const applyFilter = (f) => {
    setFilterStatus(f.status); setFilterPriority(f.priority);
    setFilterAgent(f.agent||'all'); setFilterCategory(f.category||'all');
    setSearchTerm(f.search||''); setCurrentPage(1);
  };

  const openTicketDetail = async (ticket) => {
    setSelectedTicket(ticket);
    setShowDetailModal(true);
    setActiveTab('details');
    await loadComments(ticket.TicketID, showInternalNotes);
    await loadAttachments(ticket.TicketID);
    await loadSLADetails(ticket.TicketID);
  };

  const handleReopenTicket = async () => {
    if (!selectedTicket) return;
    try {
      await API.patch(`/tickets/${selectedTicket.TicketID}/reopen`);
      setShowReopenConfirm(false); loadAllData();
      setSelectedTicket(prev => ({...prev, Status:'open'}));
      showNotification('Ticket reopened');
    } catch { showNotification('Error','error'); }
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignAgentID) { showNotification('Select an agent','error'); return; }
    try {
      await Promise.all(selectedRows.map(id => API.patch(`/tickets/${id}/assign`, { AgentID: bulkAssignAgentID })));
      setSelectedRows([]); setBulkAssignAgentID(''); setShowBulkAssignModal(false);
      loadAllData(); showNotification(`${selectedRows.length} ticket(s) assigned`);
    } catch { showNotification('Error','error'); }
  };

  const handleBulkStatus = async (newStatus) => {
    try {
      await Promise.all(selectedRows.map(id => API.patch(`/tickets/${id}/status`, { Status: newStatus })));
      setSelectedRows([]); loadAllData();
      showNotification(`${selectedRows.length} ticket(s) updated to ${newStatus}`);
    } catch { showNotification('Error','error'); }
  };

  // ── KB Actions ─────────────────────────────────────────────────────────
  const handleKBSearch = (e) => { const val = e.target.value; setKBSearch(val); loadKBArticles(val, kbCategory); };
  const handleKBCategoryChange = (cat) => { setKBCategory(cat); loadKBArticles(kbSearch, cat); };
  const openArticle = (article) => { setSelectedArticle(article); setShowArticleModal(true); };

  const handleVoteArticle = async (articleId, helpful) => {
    if (articleVoted[articleId]) return;
    try {
      await API.post(`/kb/articles/${articleId}/vote`, { helpful });
      setArticleVoted(prev => ({...prev, [articleId]: true}));
      setKBArticles(prev => prev.map(a => a.ArticleID===articleId
        ? {...a, [helpful?'HelpfulCount':'NotHelpfulCount']: (a[helpful?'HelpfulCount':'NotHelpfulCount']||0)+1} : a
      ));
      if (selectedArticle?.ArticleID===articleId) {
        setSelectedArticle(prev => ({...prev, [helpful?'HelpfulCount':'NotHelpfulCount']: (prev[helpful?'HelpfulCount':'NotHelpfulCount']||0)+1}));
      }
      showNotification(helpful ? 'Marked as helpful!' : 'Thanks for the feedback');
    } catch { showNotification('Error','error'); }
  };

  const handleCreateArticle = async (e) => {
    e.preventDefault();
    try {
      await API.post('/kb/articles', { ...newArticle, AuthorID: userRole === 'requester' ? currentUser.id : 1 });
      setShowCreateArticleModal(false);
      setNewArticle({ Title:'', Summary:'', Content:'', Category:'General', Tags:'' });
      loadKBArticles(kbSearch, kbCategory); loadKBCategories();
      showNotification('Article published');
    } catch { showNotification('Error publishing article','error'); }
  };

  // ── Chatbot Actions ────────────────────────────────────────────────────
  const handleSendChat = async () => {
    const msg = chatInput.trim();
    if (!msg || chatLoading) return;
    setChatMessages(prev => [...prev, { role:'user', content:msg, timestamp:new Date().toISOString() }]);
    setChatInput(''); setChatLoading(true);
    try {
      const history = chatMessages.map(m => ({ role: m.role, content: m.content }));
      const r = await API.post('/chatbot/message', { message: msg, conversationHistory: history });
      setChatMessages(prev => [...prev, { role:'assistant', content: r.data.reply, timestamp: r.data.timestamp }]);
    } catch {
      setChatMessages(prev => [...prev, { role:'assistant', content:"I'm having trouble right now. Please try again or create a support ticket.", timestamp: new Date().toISOString() }]);
    }
    setChatLoading(false);
  };

  const handleChatKeyDown = (e) => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); handleSendChat(); } };

  // ── SLA Actions ────────────────────────────────────────────────────────
  const handleUpdateSLAPolicy = async (policy) => {
    try {
      await API.put(`/sla-policies/${policy.PolicyID}`, { ResolutionHours: policy.ResolutionHours, FirstResponseHours: policy.FirstResponseHours });
      loadSLAPolicies(); setEditingSLA(null);
      showNotification('SLA policy updated');
    } catch { showNotification('Error updating SLA','error'); }
  };

  // ── Filtering & Pagination ─────────────────────────────────────────────
  const getBaseTickets = () => {
    if (userRole === 'requester') return tickets.filter(t => t.RequesterID === currentUser.id);
    if (userRole === 'agent')     return tickets.filter(t => t.AssignedAgentID === currentAgent.id);
    return tickets;
  };

  const getFilteredTickets = () => {
    return getBaseTickets().filter(ticket => {
      if (filterStatus!=='all'   && ticket.Status!==filterStatus)                   return false;
      if (filterPriority!=='all' && ticket.Priority!==filterPriority)               return false;
      if (filterAgent!=='all'    && String(ticket.AssignedAgentID)!==filterAgent)   return false;
      if (filterCategory!=='all' && ticket.Category!==filterCategory)               return false;
      if (searchTerm && !ticket.Title?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !ticket.TicketNumber?.toLowerCase().includes(searchTerm.toLowerCase()))    return false;
      return true;
    });
  };

  const filteredTickets = getFilteredTickets();
  const totalPages  = Math.max(1, Math.ceil(filteredTickets.length/PAGE_SIZE));
  const pagedTickets = filteredTickets.slice((currentPage-1)*PAGE_SIZE, currentPage*PAGE_SIZE);

  useEffect(() => { setCurrentPage(1); }, [filterStatus,filterPriority,filterAgent,filterCategory,searchTerm]);

  const toggleRowSelect = (id) => {
    setSelectedRows(prev => prev.includes(id) ? prev.filter(r=>r!==id) : [...prev,id]);
  };

  // ── Display helpers ────────────────────────────────────────────────────
  const getStatusBadge = (status) => {
    const map = {
      open:        { label:'Open',    cls:'badge-open'    },
      in_progress: { label:'Pending', cls:'badge-pending' },
      resolved:    { label:'Solved',  cls:'badge-solved'  },
      closed:      { label:'Closed',  cls:'badge-closed'  },
    };
    const s = map[status]||{ label:status, cls:'' };
    return <span className={`status-badge ${s.cls}`}>{s.label}</span>;
  };

  const getSLAClass = (status) => {
    if (status==='Breached')  return 'sla-breached';
    if (status==='At Risk')   return 'sla-risk';
    if (status==='Completed') return 'sla-completed';
    return 'sla-track';
  };

  const getSLACountdown = (ticket) => {
    if (!ticket.MinutesUntilDeadline && ticket.MinutesUntilDeadline !== 0) return null;
    if (ticket.Status==='resolved'||ticket.Status==='closed') return null;
    const mins = ticket.MinutesUntilDeadline;
    if (mins < 0)  return <span className="sla-countdown breached">Overdue {Math.abs(mins)}m</span>;
    if (mins < 60) return <span className="sla-countdown at-risk">{mins}m left</span>;
    const hrs = Math.floor(mins/60);
    if (hrs < 24)  return <span className="sla-countdown at-risk">{hrs}h left</span>;
    return <span className="sla-countdown on-track">{Math.floor(hrs/24)}d left</span>;
  };

  const getPriorityIcon = (priority) => {
    if (priority==='high')   return <span className="priority-icon urgent" title="High">↑↑</span>;
    if (priority==='medium') return <span className="priority-icon medium" title="Medium">↑</span>;
    return <span className="priority-icon low" title="Low">↓</span>;
  };

  const getInitials     = (name) => name ? name.split(' ').map(n=>n[0]).join('').toUpperCase().slice(0,2) : '?';
  const getAvatarColor  = (id)   => { const c=['#6366f1','#0891b2','#059669','#d97706','#dc2626','#7c3aed','#db2777']; return c[(id||0)%c.length]; };
  const getWorkloadColor= (score)=> score>=15?'#dc2626':score>=8?'#f59e0b':'#10b981';

  const myTickets = getBaseTickets();
  const sidebarCounts = {
    all:      myTickets.length,
    toHandle: myTickets.filter(t=>t.Status==='open'&&!t.AssignedAgentID).length,
    myOpen:   myTickets.filter(t=>t.Status==='open').length,
    last7:    myTickets.filter(t=>new Date(t.CreatedAt)>new Date(Date.now()-7*86400000)).length,
    open:     myTickets.filter(t=>t.Status==='open').length,
    pending:  myTickets.filter(t=>t.Status==='in_progress').length,
    solved:   myTickets.filter(t=>t.Status==='resolved').length,
    closed:   myTickets.filter(t=>t.Status==='closed').length,
  };

  const toggleInternalNotes = async (val) => {
    setShowInternalNotes(val);
    if (selectedTicket) await loadComments(selectedTicket.TicketID, val);
  };

  const categories = ['all',...Array.from(new Set(tickets.map(t=>t.Category).filter(Boolean)))];

  const getConditionValueOptions = () => {
    if (newRule.ConditionType==='priority') return [
      { value:'', label:'Select priority...' },
      { value:'high', label:'High / Urgent' },
      { value:'medium', label:'Medium' },
      { value:'low', label:'Low' },
    ];
    if (newRule.ConditionType==='category') {
      const cats = Array.from(new Set(tickets.map(t=>t.Category).filter(Boolean)));
      return [
        { value:'', label:'Select category...' },
        ...cats.map(c=>({value:c,label:c})),
        { value:'Technical', label:'Technical' },
        { value:'Billing', label:'Billing' },
        { value:'Account', label:'Account' },
        { value:'Feature', label:'Feature Request' },
        { value:'Bug', label:'Bug Report' },
      ].filter((v,i,arr)=>arr.findIndex(x=>x.value===v.value)===i);
    }
    return [];
  };

  // ── Derived label for role badge ───────────────────────────────────────
  const roleBadgeLabel = () => {
    const icon = userRole==='admin'?'⬡':userRole==='agent'?'◈':'◎';
    const name = userRole.charAt(0).toUpperCase()+userRole.slice(1);
    if (userRole==='agent')     return `${icon} ${name} — ${currentAgent.name}`;
    if (userRole==='requester') return `${icon} ${name} — ${currentUser.name}`;
    return `${icon} ${name}`;
  };

  // ── Lists for switchers ────────────────────────────────────────────────
  const requesterList = users.filter(u => u.Role === 'requester' || u.Role === 'admin');
  const agentList     = agents; // already filtered to admin/agent on backend

  // ══════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════
  return (
    <div className="app-root">
      {/* Toast */}
      {notification && (
        <div className={`toast ${notification.type}`}>
          <span className="toast-icon">{notification.type==='success'?'✓':'✕'}</span>
          {notification.msg}
        </div>
      )}

      {/* Icon Nav */}
      <nav className="icon-nav">
        <div className="icon-nav-brand"><div className="brand-dot"></div></div>
        <div className="icon-nav-top">
          <button className={`nav-icon ${activeNav==='tickets'?'active':''}`}
            onClick={() => { setActiveNav('tickets'); setShowReports(false); }} title="Tickets">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
            </svg>
            {sidebarCounts.open > 0 && <span className="nav-badge">{sidebarCounts.open>9?'9+':sidebarCounts.open}</span>}
            <span className="nav-tooltip">Tickets</span>
          </button>

          {can(userRole,'canViewAgents') && (
            <button className={`nav-icon ${activeNav==='agents'?'active':''}`}
              onClick={() => { setActiveNav('agents'); setShowReports(false); loadAgentPerformance(); loadAgentWorkload(); }} title="Agents">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <span className="nav-tooltip">Agents</span>
            </button>
          )}

          <button className={`nav-icon ${activeNav==='kb'?'active':''}`}
            onClick={() => { setActiveNav('kb'); setShowReports(false); loadKBArticles(kbSearch,kbCategory); loadKBCategories(); }} title="Knowledge Base">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
            </svg>
            <span className="nav-tooltip">Knowledge Base</span>
          </button>

          {can(userRole,'canViewReports') && (
            <button className={`nav-icon ${activeNav==='reports'?'active':''}`}
              onClick={() => { setActiveNav('reports'); setShowReports(true); }} title="Reports">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
              <span className="nav-tooltip">Reports</span>
            </button>
          )}
        </div>
        <div className="icon-nav-bottom">
          <button className={`nav-icon ${showChatbot?'active':''}`} onClick={() => setShowChatbot(v=>!v)} title="AI Assistant">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              <circle cx="9" cy="10" r="1" fill="currentColor"/>
              <circle cx="12" cy="10" r="1" fill="currentColor"/>
              <circle cx="15" cy="10" r="1" fill="currentColor"/>
            </svg>
            {chatLoading && <span className="nav-badge pulse-badge">•</span>}
            <span className="nav-tooltip">AI Assistant</span>
          </button>

          {can(userRole,'canSubmitCSAT') && (
            <button className="nav-icon" title="Rate a ticket"
              onClick={() => { if(selectedTicket) setShowCSATModal(true); else showNotification('Select a ticket first','error'); }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
              <span className="nav-tooltip">CSAT</span>
            </button>
          )}
          {can(userRole,'canManageFields') && (
            <button className="nav-icon" title="Settings" onClick={() => setCustomFieldsModal(true)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              <span className="nav-tooltip">Settings</span>
            </button>
          )}
          <div className={`role-indicator role-${userRole}`} title={`Role: ${userRole}`}></div>
        </div>
      </nav>

      {/* Left Sidebar */}
      <aside className="left-sidebar">
        {/* ── Sidebar Header ── */}
        <div className="sidebar-header">
          <div className="logo-container">
            <img src="/logo2.png" alt="ServiceNet" className="logo-image" />
          </div>
          <div className="sidebar-header-right">

            {/* ── REQUESTER SWITCHER ── shown only in requester mode */}
            {userRole === 'requester' && requesterList.length > 0 && (
              <div className="switcher-wrap">
                <label className="switcher-label">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  User
                </label>
                <select
                  className="identity-switcher"
                  value={currentUser.id}
                  onChange={e => handleSwitchRequester(e.target.value)}
                  title={`Current user: ${currentUser.name}`}
                >
                  {requesterList.map(u => (
                    <option key={u.UserID} value={u.UserID}>{u.FullName}</option>
                  ))}
                </select>
              </div>
            )}

            {/* ── AGENT SWITCHER ── shown only in agent mode */}
            {userRole === 'agent' && agentList.length > 0 && (
              <div className="switcher-wrap">
                <label className="switcher-label">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  Agent
                </label>
                <select
                  className="identity-switcher agent-switcher"
                  value={currentAgent.id}
                  onChange={e => handleSwitchAgent(e.target.value)}
                  title={`Current agent: ${currentAgent.name}`}
                >
                  {agentList.map(a => (
                    <option key={a.UserID} value={a.UserID}>{a.FullName}</option>
                  ))}
                </select>
              </div>
            )}

            {can(userRole,'canCreateTicket') && (
              <button className="btn-new-ticket" onClick={() => setShowCreateForm(true)}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                New ticket
              </button>
            )}
          </div>
        </div>

        {/* Role badge */}
        <div className="role-badge-bar">
          <span className={`role-badge role-badge-${userRole}`}>{roleBadgeLabel()}</span>
        </div>

        {/* Search */}
        <div className="sidebar-search">
          <svg className="search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input type="text" placeholder="Search tickets..." value={searchTerm} onChange={e=>setSearchTerm(e.target.value)}/>
          {searchTerm && <button className="search-clear" onClick={()=>setSearchTerm('')}>✕</button>}
        </div>

        <nav className="sidebar-nav">
          <button
            className={`sidebar-link ${filterStatus==='all'&&filterPriority==='all'&&filterAgent==='all'&&filterCategory==='all'&&!searchTerm?'active':''}`}
            onClick={() => { setFilterStatus('all');setFilterPriority('all');setFilterAgent('all');setFilterCategory('all');setSearchTerm('');setActiveNav('tickets');setShowReports(false); }}>
            <span className="link-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg></span>
            All tickets <span className="link-count">{sidebarCounts.all}</span>
          </button>

          {can(userRole,'canViewAllTickets') && (
            <button className="sidebar-link" onClick={()=>{setFilterStatus('open');setFilterAgent('all');setActiveNav('tickets');setShowReports(false);}}>
              <span className="link-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></span>
              Tickets to handle <span className="link-count">{sidebarCounts.toHandle}</span>
            </button>
          )}

          <button className="sidebar-link" onClick={()=>{setFilterStatus('open');setActiveNav('tickets');setShowReports(false);}}>
            <span className="link-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span>
            My open tickets <span className="link-count">{sidebarCounts.myOpen}</span>
          </button>

          <div className="sidebar-group">
            <span className="sidebar-group-label">MY VIEWS</span>
            <button className="sidebar-link sub" onClick={()=>{setFilterStatus('all');setFilterPriority('all');setFilterAgent('all');setFilterCategory('all');setSearchTerm('');setActiveNav('tickets');setShowReports(false);}}>
              <span className="link-icon">🕐</span>Last 7 days <span className="link-count">{sidebarCounts.last7}</span>
            </button>
          </div>

          <div className="sidebar-group">
            <span className="sidebar-group-label">
              STATUSES
              {can(userRole,'canViewAllTickets') && (
                <button className="icon-tiny" onClick={()=>{loadOverdueTickets();setShowOverdueModal(true);}} title="View overdue">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                </button>
              )}
            </span>
            {[
              {key:'open',       label:'Open',    dot:'dot-open',    count:sidebarCounts.open},
              {key:'in_progress',label:'Pending', dot:'dot-pending', count:sidebarCounts.pending},
              {key:'resolved',   label:'Solved',  dot:'dot-solved',  count:sidebarCounts.solved},
              {key:'closed',     label:'Closed',  dot:'dot-closed',  count:sidebarCounts.closed},
            ].map(({key,label,dot,count}) => (
              <button key={key} className={`sidebar-link sub status-link ${filterStatus===key?'active':''}`}
                onClick={()=>{setFilterStatus(key);setActiveNav('tickets');setShowReports(false);}}>
                <span className={`status-dot ${dot}`}></span>{label}<span className="link-count">{count}</span>
              </button>
            ))}
          </div>

          {savedFilters.length>0 && (
            <div className="sidebar-group">
              <span className="sidebar-group-label">SAVED FILTERS</span>
              {savedFilters.map((f,i) => (
                <div key={i} className="saved-filter-row">
                  <button className="sidebar-link sub" style={{flex:1}} onClick={()=>applyFilter(f)}>
                    <span className="link-icon">🔖</span>{f.name}
                  </button>
                  <button className="delete-filter-btn" onClick={()=>deleteSavedFilter(i)}>✕</button>
                </div>
              ))}
            </div>
          )}
        </nav>

        {can(userRole,'canManageRules') && (
          <div className="routing-toggle-bar">
            <div className="routing-toggle-info">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
              </svg>
              <span>Smart routing</span>
            </div>
            <div className="toggle-switch-row">
              <button className={`toggle-switch ${routingEnabled?'on':''}`} onClick={()=>setRoutingEnabled(v=>!v)}>
                <span className="toggle-thumb"></span>
              </button>
              <button className="workload-btn" onClick={()=>{loadAgentWorkload();setShowWorkloadModal(true);}}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
                </svg>
              </button>
            </div>
          </div>
        )}

        <div className="sidebar-footer">
          <div className="role-switcher">
            {['admin','agent','requester'].map(r => (
              <button key={r} className={`role-pill ${userRole===r?'active':''}`} onClick={()=>setUserRole(r)}>
                {r.charAt(0).toUpperCase()+r.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main className="main-area">

        {/* ── AGENTS VIEW ── */}
        {activeNav==='agents' && can(userRole,'canViewAgents') && !showReports && (
          <div className="content-view">
            <div className="view-header">
              <div>
                <h1 className="view-title">Agents</h1>
                <p className="view-subtitle">Team performance & workload overview</p>
              </div>
              <div style={{display:'flex',gap:8}}>
                <button className="btn-primary-action" onClick={()=>{loadAgentWorkload();setShowWorkloadModal(true);}}>📊 Workload</button>
                <button className="btn-primary-action" onClick={()=>{loadAgentPerformance();setShowLeaderboard(true);}}>🏆 Leaderboard</button>
              </div>
            </div>

            {agentWorkload.length>0 && (
              <div className="workload-cards">
                {agentWorkload.map(agent => (
                  <div key={agent.UserID} className="workload-card">
                    <div className="workload-card-header">
                      <div className="avatar-sm" style={{background:getAvatarColor(agent.UserID)}}>{getInitials(agent.FullName)}</div>
                      <div>
                        <div className="workload-name">{agent.FullName}</div>
                        <div className="workload-meta">{agent.ActiveTickets} active · {agent.HighPriorityActive} high</div>
                      </div>
                    </div>
                    <div className="workload-bar-wrap">
                      <div className="workload-bar">
                        <div className="workload-fill" style={{width:`${Math.min(100,(agent.WorkloadScore/20)*100)}%`,background:getWorkloadColor(agent.WorkloadScore)}}></div>
                      </div>
                      <span className="workload-score" style={{color:getWorkloadColor(agent.WorkloadScore)}}>{agent.WorkloadScore}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="table-card">
              <table className="data-table">
                <thead>
                  <tr><th>Agent</th><th>Email</th><th>Active</th><th>Assigned</th><th>Resolved</th><th>SLA Breaches</th><th>Workload</th><th>Compliance</th></tr>
                </thead>
                <tbody>
                  {agentPerformance.map(a => (
                    <tr key={a.UserID}>
                      <td>
                        <div className="agent-cell">
                          <div className="avatar-sm" style={{background:getAvatarColor(a.UserID)}}>{getInitials(a.FullName)}</div>
                          <span className="agent-name-text">{a.FullName}</span>
                        </div>
                      </td>
                      <td className="text-muted">{agents.find(ag=>ag.UserID===a.UserID)?.Email||'—'}</td>
                      <td><span className="metric-num">{a.ActiveTickets||0}</span></td>
                      <td><span className="metric-num">{a.TotalAssigned||0}</span></td>
                      <td><span className="metric-num resolved">{a.ResolvedCount||0}</span></td>
                      <td><span className={a.SLABreaches>0?'breach-count':'metric-num'}>{a.SLABreaches||0}</span></td>
                      <td>
                        <div className="workload-bar-wrap">
                          <div className="workload-bar">
                            <div className="workload-fill" style={{width:`${Math.min(100,((a.WorkloadScore||0)/20)*100)}%`,background:getWorkloadColor(a.WorkloadScore||0)}}></div>
                          </div>
                          <span className="workload-score" style={{color:getWorkloadColor(a.WorkloadScore||0)}}>{a.WorkloadScore||0}</span>
                        </div>
                      </td>
                      <td>
                        <div className="compliance-bar-wrap">
                          {a.ResolvedCount>0 ? (
                            <>
                              <div className="compliance-bar">
                                <div className="compliance-fill" style={{width:`${Math.round((1-(a.SLABreaches/a.ResolvedCount))*100)}%`}}></div>
                              </div>
                              <span className="compliance-pct">{Math.round((1-(a.SLABreaches/a.ResolvedCount))*100)}%</span>
                            </>
                          ) : '—'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── KNOWLEDGE BASE VIEW ── */}
        {activeNav==='kb' && (
          <div className="content-view">
            <div className="view-header">
              <div>
                <h1 className="view-title">Knowledge Base</h1>
                <p className="view-subtitle">Self-service help articles and guides</p>
              </div>
              {can(userRole,'canManageKB') && (
                <button className="btn-primary-action" onClick={()=>setShowCreateArticleModal(true)}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  New Article
                </button>
              )}
            </div>
            <div className="kb-search-row">
              <div className="kb-search-wrap">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input type="text" placeholder="Search articles, keywords, tags..." value={kbSearch} onChange={handleKBSearch} className="kb-search-input"/>
                {kbSearch && <button className="search-clear" onClick={()=>{setKBSearch('');loadKBArticles('',kbCategory);}}>✕</button>}
              </div>
              <div className="kb-cats">
                <button className={`kb-cat-pill ${kbCategory==='all'?'active':''}`} onClick={()=>handleKBCategoryChange('all')}>All</button>
                {kbCategories.map(c => (
                  <button key={c.Category} className={`kb-cat-pill ${kbCategory===c.Category?'active':''}`} onClick={()=>handleKBCategoryChange(c.Category)}>
                    {c.Category} <span className="kb-cat-count">{c.ArticleCount}</span>
                  </button>
                ))}
              </div>
            </div>
            {kbLoading ? (
              <div className="loading-state"><div className="spinner"></div><p>Loading articles...</p></div>
            ) : kbArticles.length===0 ? (
              <div className="empty-state">
                <div className="empty-icon">📖</div>
                <p>{kbSearch ? 'No articles match your search' : 'No articles yet'}</p>
                {can(userRole,'canManageKB') && <button className="btn-primary-modal" onClick={()=>setShowCreateArticleModal(true)}>Create first article</button>}
              </div>
            ) : (
              <div className="kb-grid">
                {kbArticles.map(article => (
                  <div key={article.ArticleID} className="kb-card" onClick={()=>openArticle(article)}>
                    <div className="kb-card-header">
                      <span className="kb-cat-tag">{article.Category}</span>
                      <span className="kb-views">👁 {article.ViewCount||0}</span>
                    </div>
                    <h3 className="kb-card-title">{article.Title}</h3>
                    <p className="kb-card-summary">{article.Summary||article.Content?.substring(0,120)+'...'}</p>
                    {article.Tags && (
                      <div className="kb-tags">
                        {article.Tags.split(',').slice(0,3).map(tag => <span key={tag} className="kb-tag">{tag.trim()}</span>)}
                      </div>
                    )}
                    <div className="kb-card-footer">
                      <div className="kb-votes">
                        <button className={`vote-btn helpful ${articleVoted[article.ArticleID]?'voted':''}`}
                          onClick={e=>{e.stopPropagation();handleVoteArticle(article.ArticleID,true);}}>👍 {article.HelpfulCount||0}</button>
                        <button className={`vote-btn ${articleVoted[article.ArticleID]?'voted':''}`}
                          onClick={e=>{e.stopPropagation();handleVoteArticle(article.ArticleID,false);}}>👎 {article.NotHelpfulCount||0}</button>
                      </div>
                      <span className="kb-author">{article.AuthorName||'Support Team'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── REPORTS VIEW ── */}
        {showReports && can(userRole,'canViewReports') && (
          <div className="content-view">
            <div className="view-header">
              <div><h1 className="view-title">Reports & Analytics</h1><p className="view-subtitle">Performance insights and trends</p></div>
              <div style={{display:'flex',gap:8}}>
                {can(userRole,'canManageRules') && (
                  <button className="btn-primary-action" onClick={()=>setShowSLAModal(true)} style={{background:'linear-gradient(135deg,#0891b2,#06b6d4)'}}>
                    ⏱ SLA Policies
                  </button>
                )}
                <button className="btn-primary-action" onClick={handleExport}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Export CSV
                </button>
              </div>
            </div>
            <div className="stats-strip">
              {[
                {num:stats.TotalTickets||0,     lbl:'Total',             cls:''},
                {num:stats.OpenTickets||0,       lbl:'Open',              cls:'open-pill'},
                {num:stats.InProgressTickets||0, lbl:'Pending',           cls:'pending-pill'},
                {num:stats.ResolvedTickets||0,   lbl:'Resolved',          cls:'solved-pill'},
                {num:stats.SLABreached||0,       lbl:'SLA Breach',        cls:'breach-pill'},
                {num:`${avgFirstResponse}m`,     lbl:'Avg First Response',cls:''},
                {num:`${avgResolution}h`,        lbl:'Avg Resolution',    cls:''},
              ].map((s,i) => (
                <div key={i} className={`stat-pill ${s.cls}`}>
                  <span className="stat-num">{s.num}</span>
                  <span className="stat-lbl">{s.lbl}</span>
                </div>
              ))}
            </div>
            <div className="charts-grid">
              <div className="chart-box">
                <h3>30-Day Ticket Trend</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                    <XAxis dataKey="Date" tick={{fontSize:10}}/>
                    <YAxis tick={{fontSize:10}}/>
                    <Tooltip contentStyle={{borderRadius:8,fontSize:12}}/>
                    <Legend/>
                    <Line type="monotone" dataKey="Created"  stroke="#6366f1" strokeWidth={2} dot={false}/>
                    <Line type="monotone" dataKey="Resolved" stroke="#10b981" strokeWidth={2} dot={false}/>
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-box">
                <h3>Status Distribution</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={statusBreakdown} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value"
                      label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                      {statusBreakdown.map((e,i) => <Cell key={i} fill={e.color}/>)}
                    </Pie>
                    <Tooltip/>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-box wide">
                <h3>Agent Workload (Active Tickets by Priority)</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={agentPerformance.map(a => ({
                    name: a.FullName.split(' ')[0],
                    Open:       tickets.filter(t=>t.AssignedAgentID===a.UserID&&t.Status==='open').length,
                    InProgress: tickets.filter(t=>t.AssignedAgentID===a.UserID&&t.Status==='in_progress').length,
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                    <XAxis dataKey="name" tick={{fontSize:11}}/>
                    <YAxis tick={{fontSize:11}}/>
                    <Tooltip contentStyle={{borderRadius:8,fontSize:12}}/>
                    <Legend/>
                    <Bar dataKey="Open"       fill="#6366f1" radius={[4,4,0,0]}/>
                    <Bar dataKey="InProgress" fill="#f59e0b" radius={[4,4,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* ── TICKETS VIEW ── */}
        {activeNav==='tickets' && !showReports && (
          <div className="tickets-view">
            <div className="tickets-toolbar">
              <div className="toolbar-left">
                <h1 className="view-title">
                  {filterStatus==='all'?'All tickets':filterStatus==='open'?'Open tickets':filterStatus==='in_progress'?'Pending tickets':filterStatus==='resolved'?'Solved tickets':'Closed tickets'}
                </h1>
                <span className="ticket-count-badge">{filteredTickets.length} tickets</span>
              </div>
              <div className="toolbar-right">
                {can(userRole,'canViewAllTickets') && (
                  <button className="tool-btn" onClick={()=>setShowSaveFilter(true)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                      <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
                    </svg>
                    Save filter
                  </button>
                )}
                {can(userRole,'canManageRules') && (
                  <button className="tool-btn" onClick={()=>setShowRulesModal(true)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3"/>
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                    </svg>
                    Rules
                  </button>
                )}
                {can(userRole,'canManageFields')  && <button className="tool-btn" onClick={()=>setCustomFieldsModal(true)}>Fields</button>}
                {can(userRole,'canViewReports')   && <button className="tool-btn" onClick={handleExport}>Export</button>}
                {can(userRole,'canViewAllTickets')&& <button className="tool-btn warning-btn" onClick={()=>{loadOverdueTickets();setShowOverdueModal(true);}}>⚠ Overdue</button>}
                {can(userRole,'canViewAgents')    && <button className="tool-btn gold-btn" onClick={()=>{loadAgentPerformance();setShowLeaderboard(true);}}>🏆 Board</button>}
              </div>
            </div>

            {/* Filter Bar */}
            <div className="filter-bar">
              <button className={`filter-add-btn ${showFilterPanel?'active':''}`} onClick={()=>setShowFilterPanel(v=>!v)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add filter
              </button>
              <select className="filter-select" value={filterPriority} onChange={e=>setFilterPriority(e.target.value)}>
                <option value="all">All priorities</option>
                <option value="high">High / Urgent</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              {can(userRole,'canViewAllTickets') && (
                <select className="filter-select" value={filterAgent} onChange={e=>setFilterAgent(e.target.value)}>
                  <option value="all">All agents</option>
                  {agents.map(a=><option key={a.UserID} value={a.UserID}>{a.FullName}</option>)}
                </select>
              )}
              <select className="filter-select" value={filterCategory} onChange={e=>setFilterCategory(e.target.value)}>
                {categories.map(c=><option key={c} value={c}>{c==='all'?'All categories':c}</option>)}
              </select>
              {(filterStatus!=='all'||filterPriority!=='all'||filterAgent!=='all'||filterCategory!=='all'||searchTerm) && (
                <button className="filter-clear-btn" onClick={()=>{setFilterStatus('all');setFilterPriority('all');setFilterAgent('all');setFilterCategory('all');setSearchTerm('');}}>
                  ✕ Clear
                </button>
              )}
              {can(userRole,'canBulkAction') && selectedRows.length>0 && (
                <div className="bulk-actions">
                  <span className="bulk-count">{selectedRows.length} selected</span>
                  <button className="bulk-btn" onClick={()=>setShowBulkAssignModal(true)}>Assign</button>
                  <button className="bulk-btn" onClick={()=>handleBulkStatus('resolved')}>Resolve</button>
                  <button className="bulk-btn" onClick={()=>handleBulkStatus('closed')}>Close</button>
                  <button className="bulk-btn danger" onClick={()=>setSelectedRows([])}>✕</button>
                </div>
              )}
            </div>

            {/* Expandable Filter Panel */}
            {showFilterPanel && (
              <div className="filter-panel">
                <div className="filter-panel-inner">
                  <div className="filter-panel-group">
                    <label>Status</label>
                    <div className="filter-pills">
                      {['all','open','in_progress','resolved','closed'].map(s=>(
                        <button key={s} className={`filter-pill ${filterStatus===s?'active':''}`} onClick={()=>setFilterStatus(s)}>
                          {s==='all'?'All':s==='in_progress'?'Pending':s.charAt(0).toUpperCase()+s.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="filter-panel-group">
                    <label>Priority</label>
                    <div className="filter-pills">
                      {['all','high','medium','low'].map(p=>(
                        <button key={p} className={`filter-pill ${filterPriority===p?'active':''}`} onClick={()=>setFilterPriority(p)}>
                          {p==='all'?'All':p.charAt(0).toUpperCase()+p.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="filter-panel-group">
                    <label>Category</label>
                    <div className="filter-pills">
                      {categories.map(c=>(
                        <button key={c} className={`filter-pill ${filterCategory===c?'active':''}`} onClick={()=>setFilterCategory(c)}>
                          {c==='all'?'All':c}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Ticket Table */}
            <div className="table-container">
              {loading ? (
                <div className="loading-state"><div className="spinner"></div><p>Loading tickets...</p></div>
              ) : filteredTickets.length===0 ? (
                <div className="empty-state">
                  <div className="empty-icon"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg></div>
                  <p>No tickets found</p><span>Try adjusting your filters</span>
                </div>
              ) : (
                <table className="tickets-table">
                  <thead>
                    <tr>
                      {can(userRole,'canBulkAction') && (
                        <th className="col-check">
                          <input type="checkbox"
                            onChange={e=>setSelectedRows(e.target.checked?pagedTickets.map(t=>t.TicketID):[])}
                            checked={selectedRows.length===pagedTickets.length&&pagedTickets.length>0}/>
                        </th>
                      )}
                      <th className="col-requester">REQUESTER</th>
                      <th className="col-subject">SUBJECT</th>
                      {can(userRole,'canViewAllTickets') && <th className="col-agent">AGENT</th>}
                      <th className="col-status">STATUS</th>
                      <th className="col-sla">SLA</th>
                      <th className="col-last">UPDATED</th>
                      <th className="col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedTickets.map(ticket => (
                      <tr key={ticket.TicketID}
                        className={`ticket-row ${selectedTicket?.TicketID===ticket.TicketID?'row-selected':''} ${selectedRows.includes(ticket.TicketID)?'row-checked':''}`}
                        onClick={()=>{setSelectedTicket(ticket);setShowRightPanel(true);}}>
                        {can(userRole,'canBulkAction') && (
                          <td className="col-check" onClick={e=>e.stopPropagation()}>
                            <input type="checkbox" checked={selectedRows.includes(ticket.TicketID)} onChange={()=>toggleRowSelect(ticket.TicketID)}/>
                          </td>
                        )}
                        <td className="col-requester">
                          <div className="requester-cell">
                            <div className="avatar-sm" style={{background:getAvatarColor(ticket.RequesterID)}}>{getInitials(ticket.RequesterName||'U')}</div>
                            <div className="requester-info">
                              <span className="requester-name">{ticket.RequesterName||'Unknown'}</span>
                              <span className="requester-email">{ticket.TicketNumber}</span>
                            </div>
                          </div>
                        </td>
                        <td className="col-subject">
                          <div className="subject-cell">
                            {getPriorityIcon(ticket.Priority)}
                            <span className="subject-text">{ticket.Title}</span>
                          </div>
                        </td>
                        {can(userRole,'canViewAllTickets') && (
                          <td className="col-agent">
                            {ticket.AgentName
                              ? <div className="agent-chip"><div className="agent-dot" style={{background:getAvatarColor(ticket.AssignedAgentID)}}></div>{ticket.AgentName.split(' ')[0]}</div>
                              : <span className="unassigned">—</span>}
                          </td>
                        )}
                        <td className="col-status">{getStatusBadge(ticket.Status)}</td>
                        <td className="col-sla">
                          <div style={{display:'flex',flexDirection:'column',gap:2}}>
                            <span className={`sla-tag ${getSLAClass(ticket.SLAStatus)}`}>{ticket.SLAStatus||'On Track'}</span>
                            {getSLACountdown(ticket)}
                          </div>
                        </td>
                        <td className="col-last"><span className="last-msg">{ticket.UpdatedAt?formatRelativeTime(ticket.UpdatedAt):'—'}</span></td>
                        <td className="col-actions" onClick={e=>e.stopPropagation()}>
                          <div className="row-actions">
                            <button className="action-icon" title="Quick reply" onClick={()=>{setSelectedTicket(ticket);setShowRightPanel(true);}}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                              </svg>
                            </button>
                            <button className="action-icon" title="View details" onClick={()=>openTicketDetail(ticket)}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                              </svg>
                            </button>
                            <MoreMenu ticket={ticket} userRole={userRole}
                              onStatus={(s)=>handleUpdateStatus(ticket.TicketID,s)}
                              onEscalate={()=>{setSelectedTicket(ticket);setShowEscalateModal(true);}}
                              onCSAT={()=>{setSelectedTicket(ticket);setShowCSATModal(true);}}
                              onReopen={()=>{setSelectedTicket(ticket);setShowReopenConfirm(true);}}/>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {filteredTickets.length>PAGE_SIZE && (
              <div className="pagination">
                <button className="page-btn" disabled={currentPage===1} onClick={()=>setCurrentPage(p=>Math.max(1,p-1))}>←</button>
                {Array.from({length:Math.min(totalPages,7)},(_,i)=>{
                  const page = totalPages<=7?i+1:currentPage<=4?i+1:currentPage>=totalPages-3?totalPages-6+i:currentPage-3+i;
                  return <button key={page} className={`page-btn ${currentPage===page?'active':''}`} onClick={()=>setCurrentPage(page)}>{page}</button>;
                })}
                {totalPages>7&&currentPage<totalPages-3&&<><span className="page-ellipsis">...</span><button className="page-btn" onClick={()=>setCurrentPage(totalPages)}>{totalPages}</button></>}
                <button className="page-btn" disabled={currentPage===totalPages} onClick={()=>setCurrentPage(p=>Math.min(totalPages,p+1))}>→</button>
              </div>
            )}
          </div>
        )}
      </main>
      {/* Recent comments - add this ABOVE the reply section */}

      {/* ── Right Reply Panel ── */}
      {showRightPanel && selectedTicket && (
        <aside className="reply-panel">
          <div className="reply-panel-header">
            <div className="panel-ticket-info">
              <span className="panel-ticket-num">{selectedTicket.TicketNumber}</span>
              <h3 className="panel-ticket-title">{selectedTicket.Title}</h3>
            </div>
            <button className="close-panel" onClick={()=>setShowRightPanel(false)}>✕</button>
          </div>
          <div className="panel-body">
            <div className="panel-section">
              <h4 className="panel-section-title">Ticket details</h4>
              <div className="detail-row-mini">
                <span className="detail-label">Status</span>
                {can(userRole,'canUpdateStatus') ? (
                  <select className="detail-select" value={selectedTicket.Status} onChange={e=>handleUpdateStatus(selectedTicket.TicketID,e.target.value)}>
                    <option value="open">Open</option>
                    <option value="in_progress">Pending</option>
                    <option value="resolved">Solved</option>
                    <option value="closed">Closed</option>
                  </select>
                ) : getStatusBadge(selectedTicket.Status)}
              </div>
              <div className="detail-row-mini">
                <span className="detail-label">Priority</span>
                <span className={`priority-text priority-${selectedTicket.Priority}`}>{selectedTicket.Priority}</span>
              </div>
              <div className="detail-row-mini">
                <span className="detail-label">SLA</span>
                <div style={{display:'flex',flexDirection:'column',gap:2,alignItems:'flex-end'}}>
                  <span className={`sla-tag ${getSLAClass(selectedTicket.SLAStatus)}`}>{selectedTicket.SLAStatus||'On Track'}</span>
                  {getSLACountdown(selectedTicket)}
                </div>
              </div>
              {can(userRole,'canAssignAgent') && (
                <div className="detail-row-mini">
                  <span className="detail-label">Assign to</span>
                  <select key={selectedTicket.TicketID} className="detail-select" value={selectedTicket.AssignedAgentID||''}
                    onChange={e=>{handleAssignAgent(selectedTicket.TicketID,e.target.value);setSelectedTicket(prev=>({...prev,AssignedAgentID:parseInt(e.target.value)}));}}>
                    <option value="">Unassigned</option>
                    {agents.map(a=><option key={a.UserID} value={a.UserID}>{a.FullName}</option>)}
                  </select>
                </div>
              )}
            </div>

            {cannedResponses.length>0 && can(userRole,'canUpdateStatus') && (
              <div className="panel-section">
                <h4 className="panel-section-title">Canned responses</h4>
                <div className="canned-chips">
                  {cannedResponses.map(c=>(
                    <button key={c.ResponseID} className="canned-chip" onClick={()=>applyCannedResponse(c.ResponseID)}>{c.Title}</button>
                  ))}
                </div>
              </div>
            )}

            {can(userRole,'canEscalate') && (
              <div className="panel-section">
                <h4 className="panel-section-title">Quick actions</h4>
                <button className="quick-action-btn" onClick={()=>setShowEscalateModal(true)}>↑ Escalate ticket</button>
                <button className="quick-action-btn" onClick={()=>setShowPauseTimer(true)}>⏸ Pause SLA timer</button>
                {(selectedTicket.Status==='resolved'||selectedTicket.Status==='closed') && can(userRole,'canReopenTicket') && (
                  <button className="quick-action-btn" onClick={()=>setShowReopenConfirm(true)}>↺ Reopen ticket</button>
                )}
                <button className="quick-action-btn" onClick={()=>openTicketDetail(selectedTicket)}>⊙ Open full view</button>
              </div>
            )}

            {can(userRole,'canSubmitCSAT') && (
              <div className="panel-section">
                <h4 className="panel-section-title">Satisfaction</h4>
                <button className="quick-action-btn csat-btn" onClick={()=>setShowCSATModal(true)}>★ Rate this ticket</button>
              </div>
            )}

            <div className="panel-section reply-section">
              <h4 className="panel-section-title">Write a reply</h4>
              <textarea className="reply-textarea" rows="5" placeholder="Type your message..." value={quickReply} onChange={e=>setQuickReply(e.target.value)}/>
              <div className="reply-actions">
                <button className="btn-send" onClick={handleQuickReply}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                  Send
                </button>
                <button className="btn-cancel-sm" onClick={()=>setQuickReply('')}>Clear</button>
              </div>
            </div>
          </div>
        </aside>
      )}

      {/* ── AI CHATBOT WIDGET ── */}
      {showChatbot && (
        <div className="chatbot-widget">
          <div className="chatbot-header">
            <div className="chatbot-title">
              <div className="chatbot-avatar">🤖</div>
              <div>
                <div style={{fontWeight:700,fontSize:14}}>ServiceNet AI</div>
                <div className="chatbot-status">{chatLoading?'Thinking...':'Online · Ask me anything'}</div>
              </div>
            </div>
            <button className="chatbot-close" onClick={()=>setShowChatbot(false)}>✕</button>
          </div>
          <div className="chatbot-messages">
            {chatMessages.map((msg,i) => (
              <div key={i} className={`chat-message ${msg.role==='user'?'user':'bot'}`}>
                <div className="message-bubble">
                  {msg.content}
                  <div className="message-time">{new Date(msg.timestamp).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}</div>
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="chat-message bot"><div className="message-bubble"><span className="typing-indicator">···</span></div></div>
            )}
            <div ref={chatEndRef}/>
          </div>
          {chatMessages.length<=1 && (
            <div className="chat-quick-prompts">
              {['How do I create a ticket?','What are SLA policies?','Reset my password'].map(prompt=>(
                <button key={prompt} className="chat-quick-btn" onClick={()=>{setChatInput(prompt);handleSendChat();}}>
                  {prompt}
                </button>
              ))}
            </div>
          )}
          <div className="chatbot-input">
            <input type="text" placeholder="Type a message..." value={chatInput}
              onChange={e=>setChatInput(e.target.value)} onKeyDown={handleChatKeyDown} disabled={chatLoading}/>
            <button className="chat-send" onClick={handleSendChat} disabled={chatLoading||!chatInput.trim()}>
              {chatLoading ? '...' : 'Send'}
            </button>
          </div>
        </div>
      )}

      {/* ══════════ MODALS ══════════ */}

      {/* Create Ticket */}
      {showCreateForm && (
        <div className="modal-backdrop" onClick={()=>setShowCreateForm(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>New Ticket</h2>
                <p className="modal-subtitle">{routingEnabled ? '⚡ Smart routing enabled — auto-assigns to best available agent' : 'Submit a support request'}</p>
              </div>
              <button className="modal-close-btn" onClick={()=>setShowCreateForm(false)}>✕</button>
            </div>
            <form onSubmit={handleCreateTicket} className="modal-body">
              <div className="field-group">
                <label>Subject *</label>
                <input type="text" required value={newTicket.Title} onChange={e=>setNewTicket({...newTicket,Title:e.target.value})} placeholder="Brief description of the issue"/>
              </div>
              <div className="field-group">
                <label>Description *</label>
                <textarea rows="4" required value={newTicket.Description} onChange={e=>setNewTicket({...newTicket,Description:e.target.value})} placeholder="Detailed description..."/>
              </div>
              <div className="field-row">
                <div className="field-group">
                  <label>Priority</label>
                  <select value={newTicket.Priority} onChange={e=>setNewTicket({...newTicket,Priority:e.target.value})}>
                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High / Urgent</option>
                  </select>
                </div>
                <div className="field-group">
                  <label>Category</label>
                  <select value={newTicket.Category} onChange={e=>setNewTicket({...newTicket,Category:e.target.value})}>
                    <option value="">Select category</option>
                    <option value="Technical">Technical</option><option value="Billing">Billing</option>
                    <option value="Account">Account</option><option value="Feature">Feature Request</option><option value="Bug">Bug Report</option>
                  </select>
                </div>
              </div>
              {routingEnabled && agentWorkload.length>0 && (
                <div className="routing-preview">
                  <div className="routing-preview-title">📊 Current Agent Workload</div>
                  <div className="routing-preview-agents">
                    {agentWorkload.slice(0,3).map(a=>(
                      <div key={a.UserID} className="routing-agent-row">
                        <span>{a.FullName.split(' ')[0]}</span>
                        <div className="routing-mini-bar">
                          <div style={{width:`${Math.min(100,((a.WorkloadScore||0)/20)*100)}%`,background:getWorkloadColor(a.WorkloadScore||0),height:'100%',borderRadius:4}}></div>
                        </div>
                        <span className="routing-score">{a.WorkloadScore||0}</span>
                      </div>
                    ))}
                  </div>
                  <p className="routing-note">Will be routed to agent with lowest workload score</p>
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn-ghost" onClick={()=>setShowCreateForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary-modal">Create Ticket</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ticket Detail Modal */}
      {showDetailModal && selectedTicket && (
        <div className="modal-backdrop" onClick={()=>setShowDetailModal(false)}>
          <div className="modal modal-xl" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="ticket-num-sm">{selectedTicket.TicketNumber}</span>
                <h2>{selectedTicket.Title}</h2>
              </div>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                {(selectedTicket.Status==='resolved'||selectedTicket.Status==='closed') && can(userRole,'canReopenTicket') && (
                  <button className="btn-ghost" onClick={()=>setShowReopenConfirm(true)}>↺ Reopen</button>
                )}
                <button className="modal-close-btn" onClick={()=>setShowDetailModal(false)}>✕</button>
              </div>
            </div>
            <div className="modal-tabs-bar">
              {['details','comments','attachments','sla','audit'].map(tab=>(
                <button key={tab} className={`modal-tab ${activeTab===tab?'active':''}`}
                  onClick={()=>{
                    setActiveTab(tab);
                    if(tab==='comments')    loadComments(selectedTicket.TicketID,showInternalNotes);
                    if(tab==='attachments') loadAttachments(selectedTicket.TicketID);
                    if(tab==='sla')         loadSLADetails(selectedTicket.TicketID);
                  }}>
                  {tab==='sla'?'SLA Details':tab.charAt(0).toUpperCase()+tab.slice(1)}
                  {tab==='comments'&&comments.length>0&&<span className="tab-count">{comments.length}</span>}
                </button>
              ))}
            </div>
            <div className="modal-body">
              {activeTab==='details' && (
                <div className="detail-grid">
                  <div className="detail-main">
                    <div className="field-group">
                      <label>Description</label>
                      <p className="detail-description">{selectedTicket.Description}</p>
                    </div>
                    {can(userRole,'canSubmitCSAT') && (
                      <div style={{marginTop:16}}>
                        <button className="btn-primary-modal" onClick={()=>setShowCSATModal(true)}>★ Rate this ticket</button>
                      </div>
                    )}
                  </div>
                  <div className="detail-sidebar">
                    <div className="field-group">
                      <label>Status</label>
                      {can(userRole,'canUpdateStatus') ? (
                        <select value={selectedTicket.Status} onChange={e=>{handleUpdateStatus(selectedTicket.TicketID,e.target.value);setSelectedTicket(prev=>({...prev,Status:e.target.value}));}}>
                          <option value="open">Open</option><option value="in_progress">Pending</option>
                          <option value="resolved">Solved</option><option value="closed">Closed</option>
                        </select>
                      ) : getStatusBadge(selectedTicket.Status)}
                    </div>
                    <div className="field-group"><label>Priority</label><span className={`priority-text priority-${selectedTicket.Priority}`}>{selectedTicket.Priority}</span></div>
                    <div className="field-group">
                      <label>SLA Status</label>
                      <div style={{display:'flex',flexDirection:'column',gap:4}}>
                        <span className={`sla-tag ${getSLAClass(selectedTicket.SLAStatus)}`}>{selectedTicket.SLAStatus||'On Track'}</span>
                        {getSLACountdown(selectedTicket)}
                      </div>
                    </div>
                    {can(userRole,'canAssignAgent') && (
                      <div className="field-group">
                        <label>Assigned Agent</label>
                        <select key={selectedTicket.TicketID} value={selectedTicket.AssignedAgentID||''} onChange={e=>{handleAssignAgent(selectedTicket.TicketID,e.target.value);setSelectedTicket(prev=>({...prev,AssignedAgentID:parseInt(e.target.value)}));}}>
                          <option value="">Unassigned</option>
                          {agents.map(a=><option key={a.UserID} value={a.UserID}>{a.FullName}</option>)}
                        </select>
                      </div>
                    )}
                    <div className="field-group"><label>Category</label><span>{selectedTicket.Category||'—'}</span></div>
                    <div className="field-group"><label>Requester</label><span>{selectedTicket.RequesterName||'—'}</span></div>
                    <div className="field-group"><label>Created</label><span>{selectedTicket.CreatedAt?new Date(selectedTicket.CreatedAt).toLocaleString():'—'}</span></div>
                    {selectedTicket.ResolvedAt && <div className="field-group"><label>Resolved</label><span>{new Date(selectedTicket.ResolvedAt).toLocaleString()}</span></div>}
                  </div>
                </div>
              )}

              {activeTab==='comments' && (
                <div className="comments-view">
                  {can(userRole,'canAddInternalNote') && (
                    <div className="comments-toolbar">
                      <label className="toggle-internal">
                        <input type="checkbox" checked={showInternalNotes} onChange={e=>toggleInternalNotes(e.target.checked)}/>
                        Show internal notes
                      </label>
                    </div>
                  )}
                  <div className="comments-list">
                    {comments.length===0&&<p className="empty-msg">No comments yet.</p>}
                    {comments.map(c=>(
                      <div key={c.CommentID} className={`comment-bubble ${c.IsInternal?'internal-note':''}`}>
                        <div className="comment-meta">
                          <div className="avatar-xs" style={{background:getAvatarColor(c.UserID)}}>{getInitials(c.FullName)}</div>
                          <span className="comment-author">{c.FullName}</span>
                          {c.IsInternal&&<span className="internal-tag">Internal</span>}
                          <span className="comment-time">{new Date(c.CreatedAt).toLocaleString()}</span>
                        </div>
                        <p className="comment-text">{c.CommentText}</p>
                      </div>
                    ))}
                  </div>
                  <div className="add-comment-box">
                    <textarea placeholder="Write a comment..." value={newComment} onChange={e=>setNewComment(e.target.value)} rows="3"/>
                    <div className="comment-actions">
                      <button className="btn-primary-modal" onClick={()=>handleAddComment(false)}>Send public reply</button>
                      {can(userRole,'canAddInternalNote') && <button className="btn-ghost" onClick={()=>handleAddComment(true)}>Add internal note</button>}
                    </div>
                  </div>
                </div>
              )}

              {activeTab==='attachments' && (
                <div className="attachments-view">
                  <div className="upload-box">
                    <input type="file" ref={fileInputRef} onChange={e=>setSelectedFile(e.target.files[0])} style={{display:'none'}}/>
                    <button className="btn-ghost" onClick={()=>fileInputRef.current?.click()}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                      </svg>
                      Choose file
                    </button>
                    {selectedFile&&<span className="selected-file">{selectedFile.name}</span>}
                    <button className="btn-primary-modal" onClick={handleFileUpload} disabled={!selectedFile}>Upload</button>
                  </div>
                  <div className="attachments-list">
                    {attachments.length===0&&<p className="empty-msg">No attachments.</p>}
                    {attachments.map(a=>(
                      <div key={a.AttachmentID} className="attachment-row">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                        </svg>
                        <span>{a.FileName}</span>
                        <span className="file-size">{a.FileSize?`${(a.FileSize/1024).toFixed(1)} KB`:''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab==='sla' && <SLADetailsTab slaDetails={slaDetails} ticket={selectedTicket} getSLAClass={getSLAClass}/>}
              {activeTab==='audit' && can(userRole,'canViewAllTickets') && <AuditTab ticketId={selectedTicket.TicketID}/>}
              {activeTab==='audit' && !can(userRole,'canViewAllTickets') && <p className="empty-msg">Access restricted.</p>}
            </div>
          </div>
        </div>
      )}

      {/* KB Article View Modal */}
      {showArticleModal && selectedArticle && (
        <div className="modal-backdrop" onClick={()=>setShowArticleModal(false)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div><span className="ticket-num-sm">{selectedArticle.Category}</span><h2>{selectedArticle.Title}</h2></div>
              <button className="modal-close-btn" onClick={()=>setShowArticleModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {selectedArticle.Summary && <p className="article-summary">{selectedArticle.Summary}</p>}
              <div className="article-content">{selectedArticle.Content}</div>
              {selectedArticle.Tags && (
                <div className="kb-tags" style={{marginTop:16}}>
                  {selectedArticle.Tags.split(',').map(tag=><span key={tag} className="kb-tag">{tag.trim()}</span>)}
                </div>
              )}
              <div className="article-footer">
                <div className="article-meta">
                  <span>By {selectedArticle.AuthorName||'Support Team'}</span>
                  <span>·</span><span>👁 {selectedArticle.ViewCount||0} views</span>
                </div>
                <div className="article-vote">
                  <span style={{fontSize:13,color:'var(--text-muted)'}}>Was this helpful?</span>
                  <button className={`vote-btn helpful ${articleVoted[selectedArticle.ArticleID]?'voted':''}`}
                    onClick={()=>handleVoteArticle(selectedArticle.ArticleID,true)} disabled={!!articleVoted[selectedArticle.ArticleID]}>
                    👍 Yes ({selectedArticle.HelpfulCount||0})
                  </button>
                  <button className={`vote-btn ${articleVoted[selectedArticle.ArticleID]?'voted':''}`}
                    onClick={()=>handleVoteArticle(selectedArticle.ArticleID,false)} disabled={!!articleVoted[selectedArticle.ArticleID]}>
                    👎 No ({selectedArticle.NotHelpfulCount||0})
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create KB Article Modal */}
      {showCreateArticleModal && can(userRole,'canManageKB') && (
        <div className="modal-backdrop" onClick={()=>setShowCreateArticleModal(false)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div><h2>New Knowledge Base Article</h2><p className="modal-subtitle">Help your users find answers faster</p></div>
              <button className="modal-close-btn" onClick={()=>setShowCreateArticleModal(false)}>✕</button>
            </div>
            <form onSubmit={handleCreateArticle} className="modal-body">
              <div className="field-group"><label>Title *</label><input type="text" required value={newArticle.Title} onChange={e=>setNewArticle({...newArticle,Title:e.target.value})} placeholder="e.g. How to reset your password"/></div>
              <div className="field-group"><label>Summary</label><input type="text" value={newArticle.Summary} onChange={e=>setNewArticle({...newArticle,Summary:e.target.value})} placeholder="One-line description shown in article cards"/></div>
              <div className="field-group"><label>Content *</label><textarea rows="8" required value={newArticle.Content} onChange={e=>setNewArticle({...newArticle,Content:e.target.value})} placeholder="Full article content..."/></div>
              <div className="field-row">
                <div className="field-group">
                  <label>Category</label>
                  <select value={newArticle.Category} onChange={e=>setNewArticle({...newArticle,Category:e.target.value})}>
                    <option value="General">General</option><option value="Technical">Technical</option><option value="Billing">Billing</option><option value="Account">Account</option>
                  </select>
                </div>
                <div className="field-group"><label>Tags (comma separated)</label><input type="text" value={newArticle.Tags} onChange={e=>setNewArticle({...newArticle,Tags:e.target.value})} placeholder="password, login, account"/></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-ghost" onClick={()=>setShowCreateArticleModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary-modal">Publish Article</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* SLA Policies Modal */}
      {showSLAModal && (
        <div className="modal-backdrop" onClick={()=>setShowSLAModal(false)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div><h2>SLA Policies</h2><p className="modal-subtitle">Configure response and resolution time targets</p></div>
              <button className="modal-close-btn" onClick={()=>setShowSLAModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {slaPolicies.length===0 ? (
                <div>
                  <p className="empty-msg" style={{textAlign:'left',padding:'0 0 16px'}}>Default SLA targets:</p>
                  {[{priority:'High/Urgent',resolution:4,firstResponse:1},{priority:'Medium',resolution:24,firstResponse:4},{priority:'Low',resolution:72,firstResponse:8}].map(p=>(
                    <div key={p.priority} className="sla-policy-row">
                      <div className="sla-priority-label"><span className={`priority-text priority-${p.priority.toLowerCase().split('/')[0]}`}>{p.priority}</span></div>
                      <div className="sla-details">
                        <span>First response: <strong>{p.firstResponse}h</strong></span>
                        <span>Resolution: <strong>{p.resolution}h</strong></span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  {slaPolicies.map(policy=>(
                    <div key={policy.PolicyID} className="sla-policy-row">
                      <div className="sla-priority-label"><span className={`priority-text priority-${(policy.PriorityLevel||'').toLowerCase()}`}>{policy.PriorityLevel}</span></div>
                      {editingSLA===policy.PolicyID ? (
                        <div className="sla-edit-row">
                          <div className="field-group" style={{marginBottom:0}}>
                            <label>First Response (h)</label>
                            <input type="number" min="1" defaultValue={policy.FirstResponseHours||1} onChange={e=>policy.FirstResponseHours=parseInt(e.target.value)} style={{width:80}}/>
                          </div>
                          <div className="field-group" style={{marginBottom:0}}>
                            <label>Resolution (h)</label>
                            <input type="number" min="1" defaultValue={policy.ResolutionHours} onChange={e=>policy.ResolutionHours=parseInt(e.target.value)} style={{width:80}}/>
                          </div>
                          <button className="btn-primary-modal" onClick={()=>handleUpdateSLAPolicy(policy)}>Save</button>
                          <button className="btn-ghost" onClick={()=>setEditingSLA(null)}>Cancel</button>
                        </div>
                      ) : (
                        <div className="sla-details">
                          <span>First response: <strong>{policy.FirstResponseHours||'—'}h</strong></span>
                          <span>Resolution: <strong>{policy.ResolutionHours}h</strong></span>
                          {can(userRole,'canManageRules') && <button className="btn-sm-outline" onClick={()=>setEditingSLA(policy.PolicyID)}>Edit</button>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Workload Modal */}
      {showWorkloadModal && (
        <div className="modal-backdrop" onClick={()=>setShowWorkloadModal(false)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div><h2>📊 Agent Workload</h2><p className="modal-subtitle">Real-time ticket distribution & intelligent routing status</p></div>
              <button className="modal-close-btn" onClick={()=>setShowWorkloadModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="workload-routing-info">
                <div className={`routing-status-badge ${routingEnabled?'enabled':'disabled'}`}>
                  {routingEnabled?'⚡ Intelligent Routing ACTIVE':'⏸ Intelligent Routing PAUSED'}
                </div>
                <p className="routing-desc">
                  {routingEnabled
                    ? 'New tickets are automatically assigned to the agent with the lowest weighted workload score. High priority = 3pts, Medium = 2pts, Low = 1pt.'
                    : 'Intelligent routing is currently disabled. New tickets must be manually assigned.'}
                </p>
              </div>
              <table className="data-table" style={{marginTop:16}}>
                <thead><tr><th>Agent</th><th>Active Tickets</th><th>High Priority</th><th>Workload Score</th><th>Status</th></tr></thead>
                <tbody>
                  {agentWorkload.map((a,i) => (
                    <tr key={a.UserID} className={i===0?'top-row':''}>
                      <td>
                        <div className="agent-cell">
                          <div className="avatar-sm" style={{background:getAvatarColor(a.UserID)}}>{getInitials(a.FullName)}</div>
                          <div>
                            <div>{a.FullName}</div>
                            {i===0&&routingEnabled&&<div style={{fontSize:10,color:'#10b981',fontWeight:700}}>▶ Next auto-assign</div>}
                          </div>
                        </div>
                      </td>
                      <td><span className="metric-num">{a.ActiveTickets}</span></td>
                      <td><span className={a.HighPriorityActive>0?'breach-count':'metric-num'}>{a.HighPriorityActive}</span></td>
                      <td>
                        <div className="workload-bar-wrap">
                          <div className="workload-bar" style={{width:120}}>
                            <div className="workload-fill" style={{width:`${Math.min(100,((a.WorkloadScore||0)/20)*100)}%`,background:getWorkloadColor(a.WorkloadScore||0)}}></div>
                          </div>
                          <span className="workload-score" style={{color:getWorkloadColor(a.WorkloadScore||0),fontWeight:700}}>{a.WorkloadScore||0}</span>
                        </div>
                      </td>
                      <td>
                        <span style={{fontSize:11,fontWeight:700,padding:'3px 8px',borderRadius:4,
                          background:a.WorkloadScore>=15?'#fef2f2':a.WorkloadScore>=8?'#fef3c7':'#f0fdf4',
                          color:a.WorkloadScore>=15?'#dc2626':a.WorkloadScore>=8?'#d97706':'#16a34a'}}>
                          {a.WorkloadScore>=15?'Overloaded':a.WorkloadScore>=8?'Moderate':'Available'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Overdue Modal */}
      {showOverdueModal && (
        <div className="modal-backdrop" onClick={()=>setShowOverdueModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div><h2>Overdue Tickets</h2><p className="modal-subtitle">{overdueTickets.length} tickets past SLA deadline</p></div>
              <button className="modal-close-btn" onClick={()=>setShowOverdueModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {overdueTickets.length===0
                ? <div className="empty-state-sm"><span>🎉</span><p>No overdue tickets!</p></div>
                : overdueTickets.map(t=>(
                  <div key={t.TicketID} className="overdue-row">
                    <div>
                      <strong>{t.TicketNumber}</strong> — {t.Title}
                      <p className="overdue-time">⚠ Overdue by {Math.abs(t.HoursOverdue)}h · {t.AgentName||'Unassigned'}</p>
                    </div>
                    <button className="btn-sm-outline" onClick={()=>{setShowOverdueModal(false);openTicketDetail(t);}}>View</button>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard */}
      {showLeaderboard && (
        <div className="modal-backdrop" onClick={()=>setShowLeaderboard(false)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div><h2>🏆 Agent Leaderboard</h2><p className="modal-subtitle">Top performers this period</p></div>
              <button className="modal-close-btn" onClick={()=>setShowLeaderboard(false)}>✕</button>
            </div>
            <div className="modal-body">
              <table className="data-table">
                <thead><tr><th>#</th><th>Agent</th><th>Resolved</th><th>SLA Breaches</th><th>Workload</th><th>Compliance</th></tr></thead>
                <tbody>
                  {agentPerformance.map((a,i)=>(
                    <tr key={a.UserID} className={i<3?'top-row':''}>
                      <td className="rank-cell">{i===0?'🥇':i===1?'🥈':i===2?'🥉':i+1}</td>
                      <td><div className="agent-cell"><div className="avatar-sm" style={{background:getAvatarColor(a.UserID)}}>{getInitials(a.FullName)}</div>{a.FullName}</div></td>
                      <td><span className="metric-num resolved">{a.ResolvedCount||0}</span></td>
                      <td><span className={a.SLABreaches>0?'breach-count':''}>{a.SLABreaches||0}</span></td>
                      <td>
                        <div className="workload-bar-wrap">
                          <div className="workload-bar"><div className="workload-fill" style={{width:`${Math.min(100,((a.WorkloadScore||0)/20)*100)}%`,background:getWorkloadColor(a.WorkloadScore||0)}}></div></div>
                          <span>{a.WorkloadScore||0}</span>
                        </div>
                      </td>
                      <td>{a.ResolvedCount>0?`${Math.round((1-(a.SLABreaches/a.ResolvedCount))*100)}%`:'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Rules Modal */}
      {showRulesModal && (
        <div className="modal-backdrop" onClick={()=>setShowRulesModal(false)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div><h2>Auto Assignment Rules</h2><p className="modal-subtitle">Explicit rules run before intelligent routing</p></div>
              <button className="modal-close-btn" onClick={()=>setShowRulesModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {autoRules.length===0&&<p className="empty-msg">No rules configured.</p>}
              {autoRules.map(r=>(
                <div key={r.RuleID} className="rule-row">
                  <div className="rule-info">
                    <span className="rule-condition"><span className="rule-type-tag">{r.ConditionType}</span><span className="rule-arrow">→</span><strong>{r.ConditionValue}</strong></span>
                    <span className="rule-agent">Assign to: <strong>{r.AgentName||'Unknown'}</strong></span>
                  </div>
                  {can(userRole,'canDeleteRules') && <button className="btn-sm-outline danger" onClick={()=>handleDeleteRule(r.RuleID)}>Delete</button>}
                </div>
              ))}
              {can(userRole,'canManageRules') && (
                <div className="add-rule-form">
                  <h4>Add new rule</h4>
                  <div className="field-row">
                    <div className="field-group">
                      <label>Condition Type</label>
                      <select value={newRule.ConditionType} onChange={e=>setNewRule({...newRule,ConditionType:e.target.value,ConditionValue:''})}>
                        <option value="priority">Priority</option><option value="category">Category</option>
                      </select>
                    </div>
                    <div className="field-group">
                      <label>Condition Value</label>
                      <select value={newRule.ConditionValue} onChange={e=>setNewRule({...newRule,ConditionValue:e.target.value})}>
                        {getConditionValueOptions().map(opt=><option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                    </div>
                    <div className="field-group">
                      <label>Assign to Agent</label>
                      <select value={newRule.AgentID} onChange={e=>setNewRule({...newRule,AgentID:e.target.value})}>
                        <option value="">Select agent...</option>
                        {agents.map(a=><option key={a.UserID} value={a.UserID}>{a.FullName}</option>)}
                      </select>
                    </div>
                  </div>
                  <button className="btn-primary-modal" onClick={handleAddAutoRule}>Add Rule</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom Fields Modal */}
      {customFieldsModal && can(userRole,'canManageFields') && (
        <div className="modal-backdrop" onClick={()=>setCustomFieldsModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><h2>Custom Fields</h2><button className="modal-close-btn" onClick={()=>setCustomFieldsModal(false)}>✕</button></div>
            <div className="modal-body">
              {customFields.length===0&&<p className="empty-msg">No custom fields defined yet.</p>}
              {customFields.map(f=>(
                <div key={f.FieldID} className="rule-row"><strong>{f.FieldName}</strong><span className="field-type-tag">{f.FieldType}</span></div>
              ))}
              <div className="add-rule-form">
                <h4>Add field</h4>
                <div className="field-group"><label>Field Name</label><input placeholder="e.g. Customer ID" value={newCustomField.name} onChange={e=>setNewCustomField({...newCustomField,name:e.target.value})}/></div>
                <div className="field-group">
                  <label>Field Type</label>
                  <select value={newCustomField.type} onChange={e=>setNewCustomField({...newCustomField,type:e.target.value})}>
                    <option value="text">Text</option><option value="dropdown">Dropdown</option><option value="checkbox">Checkbox</option>
                  </select>
                </div>
                {newCustomField.type==='dropdown'&&(
                  <div className="field-group"><label>Options (comma separated)</label><input placeholder="Option1, Option2" value={newCustomField.options} onChange={e=>setNewCustomField({...newCustomField,options:e.target.value})}/></div>
                )}
                <button className="btn-primary-modal" onClick={handleAddCustomField}>Add Field</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Filter */}
      {showSaveFilter && (
        <div className="modal-backdrop" onClick={()=>setShowSaveFilter(false)}>
          <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><h2>Save current filter</h2><button className="modal-close-btn" onClick={()=>setShowSaveFilter(false)}>✕</button></div>
            <div className="modal-body">
              <div className="field-group">
                <label>Filter name</label>
                <input type="text" placeholder="e.g. High priority open" value={filterName} onChange={e=>setFilterName(e.target.value)} onKeyDown={e=>e.key==='Enter'&&saveCurrentFilter()}/>
              </div>
              <div className="modal-footer">
                <button className="btn-ghost" onClick={()=>setShowSaveFilter(false)}>Cancel</button>
                <button className="btn-primary-modal" onClick={saveCurrentFilter}>Save Filter</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Escalate */}
      {showEscalateModal && (
        <div className="modal-backdrop" onClick={()=>setShowEscalateModal(false)}>
          <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><h2>Escalate Ticket</h2><button className="modal-close-btn" onClick={()=>setShowEscalateModal(false)}>✕</button></div>
            <div className="modal-body">
              <div className="field-group"><label>Reason for escalation</label><textarea rows="3" value={escalateReason} onChange={e=>setEscalateReason(e.target.value)} placeholder="Describe why this ticket needs escalation..."/></div>
              <div className="modal-footer">
                <button className="btn-ghost" onClick={()=>setShowEscalateModal(false)}>Cancel</button>
                <button className="btn-danger" onClick={handleEscalateTicket}>Escalate</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pause SLA */}
      {showPauseTimer && (
        <div className="modal-backdrop" onClick={()=>setShowPauseTimer(false)}>
          <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><h2>Pause SLA Timer</h2><button className="modal-close-btn" onClick={()=>setShowPauseTimer(false)}>✕</button></div>
            <div className="modal-body">
              <div className="field-group"><label>Reason</label><textarea rows="3" value={pauseReason} onChange={e=>setPauseReason(e.target.value)} placeholder="e.g. Waiting for customer response..."/></div>
              <div className="modal-footer">
                <button className="btn-ghost" onClick={()=>setShowPauseTimer(false)}>Cancel</button>
                <button className="btn-primary-modal" onClick={handlePauseSLA}>Pause SLA</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export */}
      {showExportModal && (
        <div className="modal-backdrop" onClick={()=>setShowExportModal(false)}>
          <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><h2>Export Tickets</h2><button className="modal-close-btn" onClick={()=>setShowExportModal(false)}>✕</button></div>
            <div className="modal-body">
              <p className="export-count">{exportData.length} tickets ready to export</p>
              <div className="modal-footer">
                <button className="btn-ghost" onClick={()=>setShowExportModal(false)}>Cancel</button>
                <button className="btn-primary-modal" onClick={downloadCSV}>Download CSV</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSAT */}
      {showCSATModal && (
        <div className="modal-backdrop" onClick={()=>setShowCSATModal(false)}>
          <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div><h2>Rate your experience</h2>{selectedTicket&&<p className="modal-subtitle">{selectedTicket.TicketNumber} — {selectedTicket.Title}</p>}</div>
              <button className="modal-close-btn" onClick={()=>setShowCSATModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="csat-row">
                {[1,2,3,4,5].map(s=>(
                  <button key={s} className={`csat-star ${csatScore>=s?'active':''}`} onClick={()=>setCSATScore(s)}>★</button>
                ))}
              </div>
              {csatScore>0&&<p className="csat-label">{['','Very unsatisfied 😞','Unsatisfied 😕','Neutral 😐','Satisfied 😊','Very satisfied 😄'][csatScore]}</p>}
              <div className="field-group"><label>Feedback (optional)</label><textarea rows="3" value={csatFeedback} onChange={e=>setCSATFeedback(e.target.value)} placeholder="Tell us more about your experience..."/></div>
              <div className="modal-footer">
                <button className="btn-ghost" onClick={()=>setShowCSATModal(false)}>Cancel</button>
                <button className="btn-primary-modal" onClick={handleCSATSubmit} disabled={csatScore===0}>Submit Rating</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Assign */}
      {showBulkAssignModal && (
        <div className="modal-backdrop" onClick={()=>setShowBulkAssignModal(false)}>
          <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><h2>Assign {selectedRows.length} ticket(s)</h2><button className="modal-close-btn" onClick={()=>setShowBulkAssignModal(false)}>✕</button></div>
            <div className="modal-body">
              <div className="field-group">
                <label>Assign to agent</label>
                <select value={bulkAssignAgentID} onChange={e=>setBulkAssignAgentID(e.target.value)}>
                  <option value="">Select agent...</option>
                  {agents.map(a=><option key={a.UserID} value={a.UserID}>{a.FullName}</option>)}
                </select>
              </div>
              <div className="modal-footer">
                <button className="btn-ghost" onClick={()=>setShowBulkAssignModal(false)}>Cancel</button>
                <button className="btn-primary-modal" onClick={handleBulkAssign}>Assign</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reopen Confirm */}
      {showReopenConfirm && (
        <div className="modal-backdrop" onClick={()=>setShowReopenConfirm(false)}>
          <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><h2>Reopen Ticket</h2><button className="modal-close-btn" onClick={()=>setShowReopenConfirm(false)}>✕</button></div>
            <div className="modal-body">
              <p>Are you sure you want to reopen <strong>{selectedTicket?.TicketNumber}</strong>?</p>
              <div className="modal-footer">
                <button className="btn-ghost" onClick={()=>setShowReopenConfirm(false)}>Cancel</button>
                <button className="btn-primary-modal" onClick={handleReopenTicket}>Reopen</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────
function MoreMenu({ ticket, userRole, onStatus, onEscalate, onCSAT, onReopen }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  return (
    <div ref={ref} style={{position:'relative'}}>
      <button className="action-icon more-icon" title="More" onClick={e=>{e.stopPropagation();setOpen(v=>!v);}}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/>
        </svg>
      </button>
      {open && (
        <div className="more-dropdown" onClick={e=>e.stopPropagation()}>
          {can(userRole,'canUpdateStatus')&&ticket.Status!=='resolved'&&ticket.Status!=='closed'&&<button onClick={()=>{onStatus('resolved');setOpen(false);}}>✓ Mark solved</button>}
          {can(userRole,'canUpdateStatus')&&ticket.Status!=='closed'&&<button onClick={()=>{onStatus('closed');setOpen(false);}}>✕ Mark closed</button>}
          {can(userRole,'canReopenTicket')&&(ticket.Status==='resolved'||ticket.Status==='closed')&&<button onClick={()=>{onReopen();setOpen(false);}}>↺ Reopen</button>}
          {can(userRole,'canEscalate')&&<button onClick={()=>{onEscalate();setOpen(false);}}>↑ Escalate</button>}
          {can(userRole,'canSubmitCSAT')&&<button onClick={()=>{onCSAT();setOpen(false);}}>★ Rate ticket</button>}
        </div>
      )}
    </div>
  );
}

function SLADetailsTab({ slaDetails, ticket, getSLAClass }) {
  if (!slaDetails) return <div className="loading-state"><div className="spinner"></div></div>;

  const formatMins = (mins) => {
    if (!mins && mins !== 0) return '—';
    const abs = Math.abs(mins);
    if (abs < 60) return `${abs}m`;
    return `${Math.floor(abs/60)}h ${abs%60}m`;
  };

  const deadlineDate  = slaDetails.SLAdeadline   ? new Date(slaDetails.SLAdeadline)   : null;
  const createdDate   = slaDetails.CreatedAt      ? new Date(slaDetails.CreatedAt)      : null;
  const totalMins     = slaDetails.ResolutionHours ? slaDetails.ResolutionHours * 60   : null;
  const elapsed       = totalMins && slaDetails.MinutesRemaining !== undefined ? totalMins - slaDetails.MinutesRemaining : null;
  const progressPct   = totalMins && elapsed ? Math.min(100, Math.max(0, (elapsed/totalMins)*100)) : 0;

  return (
    <div className="sla-details-view">
      <div className="sla-status-hero">
        <div className={`sla-hero-badge ${getSLAClass(slaDetails.SLAStatus)}`}>{slaDetails.SLAStatus||'On Track'}</div>
        <div className="sla-hero-meta">
          <span>Priority: <strong style={{textTransform:'capitalize'}}>{ticket.Priority}</strong></span>
          {slaDetails.ResolutionHours && <span>Target: <strong>{slaDetails.ResolutionHours}h resolution</strong></span>}
        </div>
      </div>
      {totalMins && (
        <div className="sla-progress-section">
          <div className="sla-progress-label"><span>Time elapsed</span><span>{formatMins(elapsed)} of {slaDetails.ResolutionHours}h</span></div>
          <div className="sla-progress-bar">
            <div className="sla-progress-fill" style={{width:`${progressPct}%`,background:slaDetails.SLAStatus==='Breached'?'#dc2626':slaDetails.SLAStatus==='At Risk'?'#f59e0b':'#10b981'}}></div>
          </div>
          <div className="sla-progress-label" style={{marginTop:4}}>
            <span></span>
            <span style={{color:slaDetails.MinutesRemaining<0?'#dc2626':slaDetails.MinutesRemaining<60?'#f59e0b':'#10b981'}}>
              {slaDetails.MinutesRemaining<0?`${formatMins(Math.abs(slaDetails.MinutesRemaining))} overdue`:`${formatMins(slaDetails.MinutesRemaining)} remaining`}
            </span>
          </div>
        </div>
      )}
      <div className="sla-timeline">
        <div className="sla-timeline-item done">
          <div className="sla-timeline-dot"></div>
          <div className="sla-timeline-content"><strong>Ticket Created</strong><span>{createdDate?.toLocaleString()}</span></div>
        </div>
        <div className={`sla-timeline-item ${slaDetails.FirstResponseAt?'done':slaDetails.SLAStatus==='Breached'?'breached':'pending'}`}>
          <div className="sla-timeline-dot"></div>
          <div className="sla-timeline-content">
            <strong>First Response</strong>
            <span>{slaDetails.FirstResponseAt
              ? `${new Date(slaDetails.FirstResponseAt).toLocaleString()} (${formatMins(slaDetails.FirstResponseMinutes)})`
              : slaDetails.FirstResponseHours?`Target: within ${slaDetails.FirstResponseHours}h`:'Pending'}</span>
          </div>
        </div>
        <div className={`sla-timeline-item ${slaDetails.ResolvedAt?'done':slaDetails.SLAStatus==='Breached'?'breached':slaDetails.SLAStatus==='At Risk'?'at-risk':'pending'}`}>
          <div className="sla-timeline-dot"></div>
          <div className="sla-timeline-content"><strong>Resolution Deadline</strong><span>{deadlineDate?.toLocaleString()}</span></div>
        </div>
        {slaDetails.ResolvedAt && (
          <div className="sla-timeline-item done">
            <div className="sla-timeline-dot"></div>
            <div className="sla-timeline-content"><strong>Resolved</strong><span>{new Date(slaDetails.ResolvedAt).toLocaleString()}</span></div>
          </div>
        )}
      </div>
    </div>
  );
}

function AuditTab({ ticketId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    API.get(`/tickets/${ticketId}/audit`)
      .then(r=>{setLogs(r.data||[]);setLoading(false);})
      .catch(()=>setLoading(false));
  }, [ticketId]);
  if (loading) return <div className="loading-state"><div className="spinner"></div></div>;
  if (!logs.length) return <p className="empty-msg">No audit entries for this ticket.</p>;
  return (
    <div className="audit-list">
      {logs.map((log,i)=>(
        <div key={i} className="audit-row">
          <div className="audit-time">{new Date(log.CreatedAt).toLocaleString()}</div>
          <div className="audit-detail">
            <strong>{log.ChangedBy||'System'}</strong> changed <em>{log.FieldChanged}</em> from <code>{log.OldValue||'—'}</code> to <code>{log.NewValue||'—'}</code>
          </div>
        </div>
      ))}
    </div>
  );
}

export default App;